import React, { useState } from 'react';
import { Download, Printer, Share2, MessageCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Acciones universales para cualquier documento PDF (factura, nota de entrega,
 * guía de despacho, comprobante de pago, reportes, etc).
 *
 * props:
 *  - fetchBlob: async () => AxiosResponse con responseType blob
 *  - filename: nombre sugerido del archivo
 *  - title: texto para compartir / whatsapp
 *  - phone: (opcional) teléfono destino para WhatsApp
 *  - size: 'sm' | 'md'
 */
export default function DocumentActions({ fetchBlob, filename = 'documento.pdf', title = 'Documento SalesPRO', phone = '', size = 'sm', compact = false }) {
  const [loading, setLoading] = useState(null);

  const getBlobUrl = async () => {
    const res = await fetchBlob();
    return URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
  };

  const handleDownload = async () => {
    setLoading('download');
    try {
      const url = await getBlobUrl();
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success('SalesPRO: Descargado');
    } catch (e) {
      toast.error('SalesPRO: Error al descargar');
    } finally { setLoading(null); }
  };

  const handlePrint = async () => {
    setLoading('print');
    try {
      const url = await getBlobUrl();
      const w = window.open(url, '_blank');
      if (w) {
        w.addEventListener('load', () => { try { w.print(); } catch (_) {} });
      } else {
        toast.error('SalesPRO: Habilita las ventanas emergentes para imprimir');
      }
    } catch (e) {
      toast.error('SalesPRO: Error al imprimir');
    } finally { setLoading(null); }
  };

  const handleShare = async () => {
    setLoading('share');
    try {
      const res = await fetchBlob();
      const file = new File([res.data], filename, { type: 'application/pdf' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title, text: title });
      } else {
        // Fallback: descargar
        const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        const a = document.createElement('a');
        a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 4000);
        toast.info('SalesPRO: Tu dispositivo no soporta compartir; se descargó el archivo');
      }
    } catch (e) {
      if (e?.name !== 'AbortError') toast.error('SalesPRO: Error al compartir');
    } finally { setLoading(null); }
  };

  const handleWhatsapp = async () => {
    // En móvil: compartir el archivo PDF mediante el menú nativo (WhatsApp aparece como opción).
    // En escritorio: abrir wa.me con texto (los navegadores de escritorio no adjuntan archivos).
    try {
      const res = await fetchBlob();
      const file = new File([res.data], filename, { type: 'application/pdf' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title, text: title });
        return;
      }
    } catch (e) {
      if (e?.name === 'AbortError') return;
    }
    // Fallback escritorio: texto por wa.me
    const text = encodeURIComponent(`${title}\n(El PDF se descargará para que lo adjuntes)`);
    const base = phone ? `https://wa.me/${phone.replace(/[^0-9]/g, '')}?text=${text}` : `https://wa.me/?text=${text}`;
    window.open(base, '_blank');
    // Y descargamos el archivo para adjuntarlo manualmente
    try { await handleDownload(); } catch (_) { /* ignore */ }
  };

  const btn = `inline-flex items-center justify-center gap-1 rounded-md border border-[#E2E8F0] bg-white hover:bg-[#F8FAFC] text-[#022C22] transition-colors ${size === 'sm' ? 'px-2.5 py-1.5 text-xs' : 'px-3 py-2 text-sm'}`;
  const ic = size === 'sm' ? 14 : 16;

  return (
    <div className="flex flex-wrap gap-1.5" data-testid="document-actions">
      <button className={btn} onClick={handleDownload} disabled={loading === 'download'} title="Descargar" aria-label="Descargar" data-testid="doc-download">
        {loading === 'download' ? <Loader2 size={ic} className="animate-spin" /> : <Download size={ic} />}
        {!compact && <span>Descargar</span>}
      </button>
      <button className={btn} onClick={handlePrint} disabled={loading === 'print'} title="Imprimir" aria-label="Imprimir" data-testid="doc-print">
        {loading === 'print' ? <Loader2 size={ic} className="animate-spin" /> : <Printer size={ic} />}
        {!compact && <span>Imprimir</span>}
      </button>
      <button className={btn} onClick={handleShare} disabled={loading === 'share'} title="Compartir" aria-label="Compartir" data-testid="doc-share">
        {loading === 'share' ? <Loader2 size={ic} className="animate-spin" /> : <Share2 size={ic} />}
        {!compact && <span>Compartir</span>}
      </button>
      <button className={`${btn} text-green-700 border-green-200 hover:bg-green-50`} onClick={handleWhatsapp} title="Enviar por WhatsApp" aria-label="Enviar por WhatsApp" data-testid="doc-whatsapp">
        <MessageCircle size={ic} />
        {!compact && <span>WhatsApp</span>}
      </button>
    </div>
  );
}
